import { useState, useRef, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard, TrendingUp, Building2, Calculator,
  Puzzle, Gift, Network, Clock, LogOut, ChevronRight,
  CalendarClock, Upload, ChevronDown, Plus,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { modules } from '../../theme/constants';

const iconMap: Record<string, React.ComponentType<any>> = {
  LayoutDashboard, TrendingUp, Building2, Calculator,
  Puzzle, Gift, Network, Clock,
};

export default function Sidebar() {
  const { user, logout, activeClient, setActiveClient } = useAuth();
  const [clientDropdownOpen, setClientDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setClientDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const clients = user?.clients || [];

  function handleSelectClient(client: { id: string; name: string; domicileState: string }) {
    setActiveClient(client);
    setClientDropdownOpen(false);
    // Reload to refresh all module data with new tenant context
    window.location.reload();
  }

  return (
    <aside className="fixed left-0 top-0 bottom-0 w-64 bg-navy-900 text-ivory-200 flex flex-col z-30">
      {/* Logo */}
      <div className="px-6 py-5 border-b border-navy-700">
        <h1 className="font-display text-xl font-semibold text-white tracking-wide">
          Osprey Legacy
        </h1>
        <p className="text-xs text-navy-400 mt-0.5 font-sans">Capital Planning</p>
      </div>

      {/* Client Selector */}
      <div className="px-3 py-3 border-b border-navy-700" ref={dropdownRef}>
        <button
          onClick={() => setClientDropdownOpen(!clientDropdownOpen)}
          className="w-full flex items-center gap-2 px-3 py-2.5 rounded-md bg-navy-800 hover:bg-navy-700 transition-colors text-left"
        >
          <div className="flex-1 min-w-0">
            {activeClient ? (
              <>
                <p className="text-sm font-sans text-ivory-100 truncate">{activeClient.name}</p>
                <p className="text-[10px] text-navy-400 font-sans">{activeClient.domicileState}</p>
              </>
            ) : (
              <p className="text-sm font-sans text-navy-400">Select a client...</p>
            )}
          </div>
          <ChevronDown size={14} className={`text-navy-400 transition-transform ${clientDropdownOpen ? 'rotate-180' : ''}`} />
        </button>

        {/* Dropdown */}
        {clientDropdownOpen && (
          <div className="mt-1 bg-navy-800 rounded-md border border-navy-600 overflow-hidden shadow-lg">
            {clients.length > 0 ? (
              <div className="max-h-48 overflow-y-auto">
                {clients.map((client) => (
                  <button
                    key={client.id}
                    onClick={() => handleSelectClient(client)}
                    className={`w-full text-left px-3 py-2.5 text-sm font-sans transition-colors ${
                      activeClient?.id === client.id
                        ? 'bg-navy-600 text-white'
                        : 'text-navy-200 hover:bg-navy-700'
                    }`}
                  >
                    <span className="truncate block">{client.name}</span>
                    <span className="text-[10px] text-navy-400">{client.domicileState}</span>
                  </button>
                ))}
              </div>
            ) : (
              <div className="px-3 py-3 text-xs text-navy-400 font-sans">
                No clients yet
              </div>
            )}
            <NavLink
              to="/add-client"
              onClick={() => setClientDropdownOpen(false)}
              className="flex items-center gap-2 px-3 py-2.5 text-sm font-sans text-gold-400 hover:bg-navy-700 transition-colors border-t border-navy-600"
            >
              <Plus size={14} />
              <span>Add Client</span>
            </NavLink>
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 px-3">
        <div className="space-y-0.5">
          {modules.map((mod) => {
            const Icon = iconMap[mod.icon];
            return (
              <NavLink
                key={mod.key}
                to={mod.path}
                end={mod.path === '/'}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-sans transition-colors duration-150 group ${
                    isActive
                      ? 'bg-navy-700 text-white'
                      : 'text-navy-300 hover:bg-navy-800 hover:text-ivory-100'
                  }`
                }
              >
                {Icon && <Icon size={18} className="flex-shrink-0" />}
                <span className="flex-1">{mod.label}</span>
                <ChevronRight size={14} className="opacity-0 group-hover:opacity-50 transition-opacity" />
              </NavLink>
            );
          })}
        </div>

        {/* Data Management Section */}
        <div className="mt-8 pt-4 border-t border-navy-700">
          <p className="px-3 mb-2 text-[10px] font-sans font-semibold text-navy-500 uppercase tracking-widest">
            Data
          </p>
          <NavLink to="/entities-mgmt" className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-sans transition-colors ${isActive ? 'bg-navy-700 text-white' : 'text-navy-300 hover:bg-navy-800'}`
          }>
            <Building2 size={16} className="flex-shrink-0" />
            <span>Entities</span>
          </NavLink>
          <NavLink to="/assets" className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-sans transition-colors ${isActive ? 'bg-navy-700 text-white' : 'text-navy-300 hover:bg-navy-800'}`
          }>
            <span className="w-4 text-center text-xs">💎</span>
            <span>Assets</span>
          </NavLink>
          <NavLink to="/liabilities-mgmt" className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-sans transition-colors ${isActive ? 'bg-navy-700 text-white' : 'text-navy-300 hover:bg-navy-800'}`
          }>
            <span className="w-4 text-center text-xs">📋</span>
            <span>Liabilities</span>
          </NavLink>
          <NavLink to="/exit-events" className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-sans transition-colors ${isActive ? 'bg-navy-700 text-white' : 'text-navy-300 hover:bg-navy-800'}`
          }>
            <CalendarClock size={16} className="flex-shrink-0" />
            <span>Exit Events</span>
          </NavLink>
          <NavLink to="/members" className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-sans transition-colors ${isActive ? 'bg-navy-700 text-white' : 'text-navy-300 hover:bg-navy-800'}`
          }>
            <span className="w-4 text-center text-xs">👥</span>
            <span>Family Members</span>
          </NavLink>

          {/* Import */}
          <div className="mt-3 pt-3 border-t border-navy-700">
            <NavLink to="/import" className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-sans transition-colors ${isActive ? 'bg-navy-700 text-white' : 'text-navy-300 hover:bg-navy-800'}`
            }>
              <Upload size={16} className="flex-shrink-0" />
              <span>Import Data</span>
            </NavLink>
          </div>
        </div>
      </nav>

      {/* Disclaimer */}
      <div className="px-4 py-3 border-t border-navy-700">
        <p className="text-[9px] text-navy-500 font-sans leading-relaxed">
          For illustrative purposes only. Not legal, tax, or financial advice. Consult qualified professionals before making estate planning or investment decisions.
        </p>
      </div>

      {/* User section */}
      <div className="px-4 py-4 border-t border-navy-700">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-navy-600 flex items-center justify-center text-xs font-sans font-semibold text-ivory-200">
            {user?.firstName?.[0]}{user?.lastName?.[0]}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-sans text-ivory-100 truncate">
              {user?.firstName} {user?.lastName}
            </p>
            <p className="text-xs text-navy-400 truncate">{user?.firmName}</p>
          </div>
          <button
            onClick={logout}
            className="p-1.5 text-navy-400 hover:text-ivory-200 transition-colors"
            title="Sign out"
          >
            <LogOut size={16} />
          </button>
        </div>
      </div>
    </aside>
  );
}
